<template>
    <div class="mt-8">
        <h3 class="pb-2 mb-4 text-lg font-medium text-gray-700 border-b">
            Sesi Aktif
        </h3>
        <p class="mb-4 text-sm text-gray-500">
            Perangkat yang sedang login ke akun kamu. Keluarkan perangkat yang tidak kamu kenali.
        </p>

        <!-- Loading -->
        <div v-if="isLoading && !sessions.length" class="py-6 text-center text-gray-500">
            <AppIcon icon="mdi:loading" class="inline-block w-5 h-5 mr-2 animate-spin" /> Memuat sesi...
        </div>

        <!-- Empty -->
        <div v-else-if="!sessions.length" class="py-6 text-center text-gray-500">
            Tidak ada sesi aktif.
        </div>

        <!-- List -->
        <div v-else class="space-y-3">
            <div
                v-for="s in pagedSessions"
                :key="s.Id"
                class="flex items-center gap-4 p-4 border rounded-lg"
                :class="[s.IsCurrent ? 'border-blue-500 bg-blue-50' : 'border-gray-200', s.IsExpired ? 'opacity-60' : '']"
            >
                <AppIcon :icon="deviceIcon(s.UserAgent)" class="flex-shrink-0 w-6 h-6 text-gray-500" />
                <div class="flex-1 min-w-0">
                    <div class="flex flex-wrap items-center gap-2 font-medium text-gray-800">
                        {{ parseUA(s.UserAgent) }}
                        <span v-if="s.IsCurrent" class="px-2 py-0.5 text-xs font-semibold text-white bg-blue-500 rounded-full">Perangkat ini</span>
                        <span v-else-if="s.IsExpired" class="px-2 py-0.5 text-xs font-semibold text-red-500 bg-red-100 rounded-full">Sudah keluar</span>
                    </div>
                    <div class="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                        <span><AppIcon icon="mdi:web" class="inline w-3.5 h-3.5 mr-1" />{{ s.IpAddress || '-' }}</span>
                        <span><AppIcon icon="mdi:login" class="inline w-3.5 h-3.5 mr-1" />Login {{ formatDate(s.CreatedOn) }}</span>
                        <span><AppIcon icon="mdi:clock-outline" class="inline w-3.5 h-3.5 mr-1" />Aktif {{ fromNow(s.LastAccess) }}</span>
                    </div>
                </div>
                <button
                    v-if="!s.IsCurrent && !s.IsExpired"
                    :disabled="revokingId === s.Id"
                    @click="confirmRevoke(s)"
                    class="flex items-center gap-1 px-2 py-1 text-sm font-semibold text-red-500 rounded hover:underline disabled:opacity-50"
                >
                    <AppIcon :icon="revokingId === s.Id ? 'mdi:loading' : 'mdi:logout'" :class="revokingId === s.Id ? 'w-4 h-4 animate-spin' : 'w-4 h-4'" />
                    Keluarkan
                </button>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="flex items-center justify-center gap-4 pt-2">
                <button
                    class="flex items-center justify-center w-8 h-8 border rounded-md disabled:opacity-40"
                    :disabled="currentPage === 1"
                    @click="currentPage--"
                >
                    <AppIcon icon="mdi:chevron-left" class="w-4 h-4" />
                </button>
                <span class="text-sm text-gray-500">Halaman {{ currentPage }} / {{ totalPages }}</span>
                <button
                    class="flex items-center justify-center w-8 h-8 border rounded-md disabled:opacity-40"
                    :disabled="currentPage === totalPages"
                    @click="currentPage++"
                >
                    <AppIcon icon="mdi:chevron-right" class="w-4 h-4" />
                </button>
            </div>
        </div>

        <div v-if="otherSessionCount > 0" class="pt-4 mt-4 border-t">
            <button
                :disabled="isRevokingAll"
                @click="revokeAllShow = true"
                class="flex items-center gap-1 text-sm font-semibold text-red-500 rounded hover:underline disabled:opacity-50"
            >
                <AppIcon :icon="isRevokingAll ? 'mdi:loading' : 'mdi:power'" :class="isRevokingAll ? 'w-4 h-4 animate-spin' : 'w-4 h-4'" />
                Keluarkan semua perangkat lain
            </button>
        </div>

        <p v-if="errorMsg" class="mt-3 text-sm text-red-500">{{ errorMsg }}</p>

        <!-- Confirm single -->
        <BaseModal v-if="revokeTarget" title="Keluarkan Perangkat" @close="revokeTarget = null">
            <p class="text-gray-700">
                Keluarkan sesi <b>{{ parseUA(revokeTarget.UserAgent) }}</b>? Perangkat itu harus login ulang.
            </p>
            <div class="flex justify-end gap-2 mt-4">
                <button class="px-4 py-2 text-gray-700 border rounded-lg" @click="revokeTarget = null">Batal</button>
                <button class="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50" :disabled="!!revokingId" @click="doRevoke">Keluarkan</button>
            </div>
        </BaseModal>

        <!-- Confirm all -->
        <BaseModal v-if="revokeAllShow" title="Keluarkan Semua Perangkat Lain" @close="revokeAllShow = false">
            <p class="text-gray-700">
                Semua perangkat lain ({{ otherSessionCount }}) akan dikeluarkan. Perangkat ini tetap login.
            </p>
            <div class="flex justify-end gap-2 mt-4">
                <button class="px-4 py-2 text-gray-700 border rounded-lg" @click="revokeAllShow = false">Batal</button>
                <button class="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50" :disabled="isRevokingAll" @click="doRevokeAll">Keluarkan Semua</button>
            </div>
        </BaseModal>
    </div>
</template>

<script>
import moment from "moment";
import BaseModal from "@/components/base/BaseModal.vue";
import { parseUserAgent } from "@/utils/userAgent";

export default {
    name: "SessionManager",
    components: { BaseModal },
    data() {
        return {
            sessions: [],
            isLoading: false,
            revokingId: null,
            isRevokingAll: false,
            revokeTarget: null,
            revokeAllShow: false,
            currentPage: 1,
            pageSize: 5,
            errorMsg: "",
        };
    },
    computed: {
        otherSessionCount() {
            return this.sessions.filter((s) => !s.IsCurrent && !s.IsExpired).length;
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.sessions.length / this.pageSize));
        },
        pagedSessions() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.sessions.slice(start, start + this.pageSize);
        },
    },
    async mounted() {
        await this.loadSessions();
    },
    methods: {
        async loadSessions() {
            this.isLoading = true;
            this.errorMsg = "";
            try {
                this.sessions = (await this.$store.dispatch("user/getSessions")) || [];
                if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
            } catch (err) {
                this.errorMsg = err.response?.data?.msg || "Gagal memuat sesi.";
            } finally {
                this.isLoading = false;
            }
        },
        confirmRevoke(session) {
            this.revokeTarget = session;
        },
        async doRevoke() {
            const target = this.revokeTarget;
            if (!target || this.revokingId) return;
            this.revokingId = target.Id;
            try {
                await this.$store.dispatch("user/revokeSession", target.Id);
                this.revokeTarget = null;
                await this.loadSessions();
            } catch (err) {
                this.errorMsg = err.response?.data?.msg || "Gagal mengeluarkan perangkat.";
            } finally {
                this.revokingId = null;
            }
        },
        async doRevokeAll() {
            if (this.isRevokingAll) return;
            this.isRevokingAll = true;
            try {
                await this.$store.dispatch("user/revokeOtherSessions", "others");
                this.revokeAllShow = false;
                await this.loadSessions();
            } catch (err) {
                this.errorMsg = err.response?.data?.msg || "Gagal mengeluarkan perangkat.";
            } finally {
                this.isRevokingAll = false;
            }
        },
        parseUA(ua) {
            const { browser, os } = parseUserAgent(ua);
            return os ? `${browser} · ${os}` : browser;
        },
        deviceIcon(ua) {
            const { os } = parseUserAgent(ua);
            return ["Android", "iOS"].includes(os) ? "mdi:cellphone" : "mdi:monitor";
        },
        formatDate(d) {
            return d ? moment(d).format("DD MMM YYYY, HH:mm") : "-";
        },
        fromNow(d) {
            return d ? moment(d).fromNow() : "-";
        },
    },
};
</script>
